/**
 *
 * PersonalEmailContent
 *
 */

import React, { useEffect, useState, useRef } from 'react';
import _classNames from 'classnames';
import { useDispatch, useSelector } from 'react-redux';
import { useFirebase } from 'react-redux-firebase';
import { toast } from 'react-toastify';
import _get from 'lodash/get';
import _isEmpty from 'lodash/isEmpty';
import { makeStyles } from '@material-ui/core/styles';
import Box from '@material-ui/core/Box';
import Button from '@material-ui/core/Button';
import ButtonBase from '@material-ui/core/ButtonBase';
import IconButton from '@material-ui/core/IconButton';
import CloseIcon from '@material-ui/icons/Close';
import InputBase from '@material-ui/core/InputBase';
import Typography from '@material-ui/core/Typography';
import Grid from '@material-ui/core/Grid';
import {
  hidePersonalEmailModal,
  userSkippedPersonalEmailModal,
} from 'containers/Main/actions';
import {
  makeSelectClientDetails,
  makeSelectShowLanguageBanner,
} from 'containers/Main/selectors';
import { submitSubscription } from 'containers/Auth/actions';
import {
  makeSelectAuth,
  makeSelectProfile,
  makeSelectSubscriptionProcessing,
  makeSelectSubscriptionResult,
} from 'containers/Auth/selectors';
import usePersonalization from 'components/Hooks/usePersonalization';
import LoadingOverlay from 'components/LoadingOverlay';
import SubscriptionOptions from 'components/SubscriptionOptions';
import useSiteCopySelector from 'components/useSiteCopySelector';
import {
  emailRegex,
  getLowerCaseEmailDomain,
  getUserFirstName,
} from 'utils/stringUtils';
import { resolvePageCopy } from '../utils';

const useStyles = makeStyles(theme => ({
  themedDialogWrapper: {
    backgroundColor: 'white',
    [theme.breakpoints.up('md')]: {
      width: 650,
    },
  },
  regularWrapper: {
    backgroundColor: 'white',
    height: 'min-content',
    maxHeight: '90%',
    borderRadius: 5,
    overflowY: 'auto',
    position: 'absolute',
    [theme.breakpoints.up('md')]: {
      width: 800,
      left: 0,
      right: 0,
      top: 0,
      bottom: 0,
      margin: 'auto',
    },
    [theme.breakpoints.up('lg')]: {
      width: 995,
      left: 0,
      right: 0,
      top: 0,
      bottom: 0,
      margin: 'auto',
    },
    [theme.breakpoints.down('sm')]: {
      width: '80%',
      left: 0,
      right: 0,
      top: 0,
      bottom: 0,
      margin: 'auto',
    },
    [theme.breakpoints.down('xs')]: {
      width: '90%',
      left: 0,
      right: 0,
      top: 0,
      bottom: 0,
      margin: 'auto',
    },
  },
  paddedWrapper: {
    top: 36,
  },
  contentContainer: {
    position: 'relative',
    padding: '56px 100px 32px',
    [theme.breakpoints.down('xs')]: {
      padding: '32px',
    },
  },
  closeIcon: {
    position: 'absolute',
    top: 0,
    right: 0,
    padding: 0,
    margin: 16,
  },
  title: props => {
    const customStyles = {};
    if (_get(props, 'header.color')) {
      customStyles.color = _get(props, 'header.color');
    }
    if (_get(props, 'header.fontSize')) {
      customStyles.fontSize = _get(props, 'header.fontSize');
    }
    if (_get(props, 'header.lineHeight')) {
      customStyles.lineHeight = _get(props, 'header.lineHeight');
    }
    return {
      ...customStyles,
      [theme.breakpoints.down('xs')]: {
        fontSize: '1.5rem',
      },
    };
  },
  bodyWrapper: props => {
    const customStyles = {};
    if (_get(props, 'body.marginBottom')) {
      customStyles.marginBottom = _get(props, 'body.marginBottom');
    }

    return {
      ...customStyles,
      marginTop: 24,
      [theme.breakpoints.down('xs')]: {
        '& > p': {
          fontSize: '1rem',
        },
      },
    };
  },
  body: props => {
    const customStyles = {};
    if (_get(props, 'body.color')) {
      customStyles.color = _get(props, 'body.color');
    }
    if (_get(props, 'body.fontSize')) {
      customStyles.fontSize = _get(props, 'body.fontSize');
    }
    if (_get(props, 'body.lineHeight')) {
      customStyles.lineHeight = _get(props, 'body.lineHeight');
    }
    return customStyles;
  },
  inputWrapper: {
    display: 'flex',
    justifyContent: 'center',
    marginTop: 12,
  },
  input: {
    backgroundColor: '#FFFFFF',
    borderRadius: 4,
    padding: '8px 20px',
    border: '1px solid #D8D8D8',
    width: '80%',
    maxWidth: 440,
    ...theme.typography.body2,
    [theme.breakpoints.down('600')]: {
      width: '100%',
    },
  },
  subscriptionWrapper: {
    display: 'flex',
    justifyContent: 'center',
    marginTop: 12,
  },
  subscriptionContainer: {
    width: '80%',
    maxWidth: 440,
  },
  disclaimerWrapper: {
    marginTop: 24,
  },
  disclaimer: {
    fontFamily: 'Open Sans',
    fontStyle: 'italic',
    fontSize: '0.875rem',
  },
  actionContainer: {
    position: 'relative',
    borderTop: '1px solid #E3E3E3',
    padding: '20px 0 25px',
    display: 'flex',
    justifyContent: 'center',
  },
  skipButton: {
    ...theme.typography.body2,
    color: theme.palette.primary.main,
    height: 43,
    position: 'absolute',
    left: 32,
    '&:hover': {
      opacity: 0.8,
    },
  },
  submitButton: {
    ...theme.typography.button,
    color: '#fff',
    boxShadow: 'none',
    width: 139,
    height: 43,
    borderRadius: 2,
    '&:hover': {
      backgroundColor: theme.palette.primary.secondary,
      opacity: 0.8,
    },
  },
  subscribeButtonContainer: {
    display: 'flex',
    justifyContent: 'center',
    margin: '16px 0',
  },
  subscribeButton: {
    width: '80%',
    maxWidth: 440,
    borderRadius: 4,
    backgroundColor: theme.palette.primary.main,
  },
}));

const PersonalEmailContent = ({ type, shouldUseThemedDialog }) => {
  const dispatch = useDispatch();
  const firebase = useFirebase();
  const auth = useSelector(makeSelectAuth());
  const profile = useSelector(makeSelectProfile());
  const clientDetails = useSelector(makeSelectClientDetails());
  const subscriptionProcessing = useSelector(
    makeSelectSubscriptionProcessing(),
  );
  const subscriptionResult = useSelector(makeSelectSubscriptionResult());
  const showLanguageBanner = useSelector(makeSelectShowLanguageBanner());
  const [siteCopy] = useSiteCopySelector(['personal-email-modal']);
  const [email, setEmail] = useState('');
  const [error, setError] = useState(false);
  const emailPrefilled = useRef(false);
  const classes = useStyles(
    _get(clientDetails, 'metadata.subscriptionModal.styles', {}),
  );

  const { initialSubscriptionValues } = usePersonalization();
  const [subscription, setSubscription] = useState(initialSubscriptionValues);

  const workEmailDomains = _get(
    clientDetails,
    'context.requiredEmailDomains',
    [],
  );

  const subscriptionModal = _get(
    clientDetails,
    'metadata.subscriptionModal',
    {},
  );

  const {
    header,
    description,
    disclaimer,
    skipButtonText,
    submitButtonText,
    generalErrorMessage,
    errorMessage,
    inputPlaceholder,
    useCloseButton,
    subscribeButton,
    emailPrefill,
    allowAccountEmailUse,
  } = resolvePageCopy({ subscriptionModal, siteCopy, type });

  useEffect(() => {
    if (emailPrefill && profile && profile.email && !emailPrefilled.current) {
      setEmail(profile.email);
      emailPrefilled.current = true;
    }
  }, [emailPrefill, profile]);

  useEffect(() => {
    if (
      !_isEmpty(subscriptionResult) &&
      _get(subscriptionResult, 'error') === false
    ) {
      firebase.updateProfile({
        personalEmail: email,
      });
      dispatch(hidePersonalEmailModal());
    } else if (
      !_isEmpty(subscriptionResult) &&
      _get(subscriptionResult, 'error') === true
    ) {
      toast(allowAccountEmailUse ? generalErrorMessage : errorMessage, {
        type: 'error',
      });
    }
  }, [subscriptionResult]);

  const handleSubscriptionChange = next => {
    setSubscription(next);
  };

  const handleClose = () => {
    dispatch(userSkippedPersonalEmailModal());
  };

  const handleChange = e => {
    setEmail(e.target.value);
    if (!emailRegex.test(e.target.value)) {
      setError(true);
    } else if (error) {
      setError(false);
    }
  };

  const handleSubmit = () => {
    if (
      (allowAccountEmailUse && email === profile.email) ||
      (!allowAccountEmailUse &&
        !workEmailDomains.includes(getLowerCaseEmailDomain(email)) &&
        email !== profile.email)
    ) {
      if (!auth.isEmpty) {
        dispatch(
          submitSubscription({
            email,
            subscription,
            isPersonalEmail: true,
            allowAccountEmailUse,
          }),
        );
      }
    } else {
      toast(errorMessage, {
        type: 'error',
      });
      setError(true);
    }
  };

  const firstName = getUserFirstName(profile);

  return (
    <Grid
      container
      className={_classNames({
        [classes.themedDialogWrapper]: shouldUseThemedDialog,
        [classes.regularWrapper]: !shouldUseThemedDialog,
        [classes.paddedWrapper]: showLanguageBanner,
      })}
    >
      <LoadingOverlay show={subscriptionProcessing} />
      <Grid item xs={12}>
        <Grid container>
          <Grid item xs={12} className={classes.contentContainer}>
            {useCloseButton && (
              <IconButton
                className={classes.closeIcon}
                onClick={handleClose}
                aria-label="Close"
                disableRipple
                disableFocusRipple
              >
                <CloseIcon />
              </IconButton>
            )}
            <Grid container>
              <Grid item xs={12}>
                <Typography
                  variant="h3"
                  color="primary"
                  align="center"
                  className={classes.title}
                >
                  {header.replace('<firstName>', firstName)}
                </Typography>
              </Grid>
              {!_isEmpty(description) && (
                <Grid item xs={12} className={classes.bodyWrapper}>
                  {description.map(paragraph => (
                    <Typography
                      variant="body1"
                      color="textPrimary"
                      align="center"
                      className={classes.body}
                      key={paragraph}
                    >
                      {paragraph}
                    </Typography>
                  ))}
                </Grid>
              )}
              <Grid item xs={12} className={classes.inputWrapper}>
                <InputBase
                  type="text"
                  className={classes.input}
                  value={email}
                  onChange={handleChange}
                  placeholder={inputPlaceholder}
                  error={error}
                />
              </Grid>
              {subscribeButton && (
                <Grid item xs={12} className={classes.subscribeButtonContainer}>
                  <Button
                    variant="contained"
                    disabled={error || !email || subscriptionProcessing}
                    className={_classNames(
                      classes.submitButton,
                      classes.subscribeButton,
                    )}
                    onClick={handleSubmit}
                  >
                    {subscribeButton}
                  </Button>
                </Grid>
              )}
              <Grid item xs={12} className={classes.subscriptionWrapper}>
                <Box className={classes.subscriptionContainer}>
                  <SubscriptionOptions
                    subscription={subscription}
                    onChange={handleSubscriptionChange}
                    isSmall
                    useDisclaimerLabel
                  />
                </Box>
              </Grid>
              {!_isEmpty(disclaimer) && (
                <Grid item xs={12} className={classes.disclaimerWrapper}>
                  {disclaimer.map(paragraph => (
                    <Typography
                      color="textPrimary"
                      align="center"
                      className={classes.disclaimer}
                      key={paragraph}
                    >
                      {paragraph}
                    </Typography>
                  ))}
                </Grid>
              )}
            </Grid>
          </Grid>
          {!_isEmpty(skipButtonText || submitButtonText) && (
            <Grid item xs={12} className={classes.actionContainer}>
              {skipButtonText && (
                <ButtonBase
                  className={classes.skipButton}
                  onClick={handleClose}
                >
                  {skipButtonText}
                </ButtonBase>
              )}
              {submitButtonText && (
                <Button
                  variant="contained"
                  disabled={error || !email || subscriptionProcessing}
                  className={classes.submitButton}
                  onClick={handleSubmit}
                  color="secondary"
                >
                  {submitButtonText}
                </Button>
              )}
            </Grid>
          )}
        </Grid>
      </Grid>
    </Grid>
  );
};

export default PersonalEmailContent;
